import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  acceptGroup,
  askToJoinGroup,
  createGroup,
  searchGroups,
  withdrawGroupAsk,
  type GroupFound,
} from '../transport/api'
import { groupErrorText } from './groupText'
import { GroupFace } from './GroupFace'
import '../friends/friends.css'
import './groups.css'

/**
 * ＋ → GROUP — owner, 2026-10-18: 在 + Plus 這個按鈕下面再加入一個新的讓使用者可以創建 Group 的功能.
 *
 * Two things, in the order a person comes for them: start a group (a name, and whether strangers can
 * find it — 群組可以設定為隱藏或是可以被搜尋的), and find one that is searchable and ask to join (「其他的
 * 人就可以, 在群組不主動發出通知的狀況下, 也有機會搜尋到群組」). The owner then decides who gets in, in
 * the menu's Groups card, where the new group opens.
 */
export function CreateGroupSection({ onCreated }: { onCreated: (gid: string, name: string) => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [searchable, setSearchable] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const create = async () => {
    const n = name.trim()
    if (!n) return
    setErr(null)
    setBusy(true)
    try {
      const r = await createGroup(n, searchable ? 'searchable' : 'hidden')
      void qc.invalidateQueries({ queryKey: ['groups'] })
      onCreated(r.gid, n)
    } catch (e) {
      setErr(groupErrorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="grp-create">
      <form
        className="grp-form"
        onSubmit={(e) => {
          e.preventDefault()
          void create()
        }}
      >
        <input
          className="nas-input"
          placeholder="Group name"
          aria-label="Group name"
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <label className="grp-toggle">
          <input type="checkbox" checked={searchable} onChange={(e) => setSearchable(e.target.checked)} />
          <span>
            <span className="grp-toggle-label">Searchable</span>
            <span className="grp-sub">Anyone can find it by name and ask to join; you decide who gets in.</span>
          </span>
        </label>
        {/* In sight under a keyboard (owner, 2026-10-28) — .sheet-stick. */}
        <div className="svr-createrow svr-creatego sheet-stick">
          <button type="submit" className="sheet-btn primary" disabled={busy || !name.trim()}>
            Create group
          </button>
        </div>
        {err && (
          <p className="err-line gh-error" role="alert">
            {err}
          </p>
        )}
      </form>
      <FindGroup />
    </div>
  )
}

/** Searchable groups by name, each with the one button where the viewer stands. */
function FindGroup() {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [found, setFound] = useState<GroupFound[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const search = async () => {
    const text = q.trim()
    if (text.length < 2) return
    setErr(null)
    setBusy(true)
    try {
      setFound((await searchGroups(text)).groups)
    } catch (e) {
      setErr(groupErrorText(e))
    } finally {
      setBusy(false)
    }
  }
  const act = async (g: GroupFound, fn: () => Promise<{ ok: boolean; status?: string }>, next: GroupFound['status']) => {
    setErr(null)
    setBusy(true)
    try {
      const r = await fn()
      const status = r.status === 'joined' ? 'member' : next
      setFound((list) => (list ?? []).map((x) => (x.gid === g.gid ? { ...x, status } : x)))
      void qc.invalidateQueries({ queryKey: ['groups'] })
    } catch (e) {
      setErr(groupErrorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="grp-find">
      <div className="gh-sec grp-h">Find a group</div>
      <form
        className="grp-search"
        onSubmit={(e) => {
          e.preventDefault()
          void search()
        }}
      >
        <input
          className="nas-input"
          type="search"
          inputMode="search"
          placeholder="Group name"
          aria-label="Find a group by name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit" className="sheet-btn" disabled={busy || q.trim().length < 2}>
          Find
        </button>
      </form>
      {found && found.length === 0 && <p className="fr-empty">No searchable group by that name.</p>}
      {found?.map((g) => (
        <div className="fr-card" key={g.gid}>
          <GroupFace icon={g.icon} />
          <span className="fr-meta">
            <span className="fr-name">{g.name}</span>
            <span className="fr-sub">
              {g.members} {g.members === 1 ? 'member' : 'members'}
            </span>
          </span>
          {g.status === 'none' && (
            <button
              type="button"
              className="fr-btn ok"
              disabled={busy}
              onClick={() => void act(g, () => askToJoinGroup(g.gid), 'asked')}
            >
              Ask to join
            </button>
          )}
          {g.status === 'asked' && (
            <button
              type="button"
              className="fr-btn"
              disabled={busy}
              onClick={() => void act(g, () => withdrawGroupAsk(g.gid), 'none')}
            >
              Asked · cancel
            </button>
          )}
          {g.status === 'invited' && (
            <button
              type="button"
              className="fr-btn ok"
              disabled={busy}
              onClick={() => void act(g, () => acceptGroup(g.gid), 'member')}
            >
              Join
            </button>
          )}
          {g.status === 'member' && <span className="fr-state friends">Member ✓</span>}
        </div>
      ))}
      {err && <p className="gh-note mch-err">{err}</p>}
    </div>
  )
}
